import Vue from 'vue';
import Vuex from 'vuex';
import { ethers } from 'ethers';

Vue.use(Vuex);

function normalizeAddress(address) {
  return String(address || '').toLowerCase();
}

function hasValidInviterValue(value) {
  if (value && typeof value === 'object') {
    return hasValidInviterValue(value.id || value.user_id || value.uid || value.address || value.wallet_address);
  }
  const str = String(value || '').trim().toLowerCase();
  return !!str && str !== '0' && str !== 'null' && str !== 'undefined' && str !== '0x0000000000000000000000000000000000000000';
}

function isAccountInviterBound(account) {
  if (!account) return false;
  const candidates = [
    account.parent_id,
    account.parentId,
    account.pid,
    account.p_id,
    account.inviter_id,
    account.inviterId,
    account.referrer_id,
    account.referrerId,
    account.recommend_id,
    account.recommendId,
    account.parent,
    account.inviter,
    account.referrer,
    account.recommend
  ];
  return candidates.some(hasValidInviterValue);
}

function getLocalBoundAddress() {
  return normalizeAddress(localStorage.getItem('doge_inviter_bound_address'));
}

let dogeAuthPromise = null;
let userDetailsPromise = null;

function withTimeout(promise, ms, message) {
  let timer;
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error(message || '请求超时，请重试')), ms);
    })
  ]).finally(() => clearTimeout(timer));
}

async function signDogeMessage(address, message) {
  if (!window.ethereum) throw new Error('请先连接钱包');
  const hexMessage = ethers.utils.hexlify(ethers.utils.toUtf8Bytes(message));
  try {
    return await withTimeout(window.ethereum.request({
      method: 'personal_sign',
      params: [hexMessage, address]
    }), 45000, '钱包签名超时，请返回钱包确认签名后重试');
  } catch (e) {
    // 用户拒绝或超时直接抛出；仅在钱包不支持 personal_sign 时再回退 ethers。
    if (e && ![-32601, -32603, 4200].includes(Number(e.code))) throw e;
    const provider = new ethers.providers.Web3Provider(window.ethereum);
    const signer = provider.getSigner();
    return withTimeout(signer.signMessage(message), 45000, '钱包签名超时，请返回钱包确认签名后重试');
  }
}

function decodeDogeAuthPayload(token) {
  const parts = String(token || '').split('.');
  if (parts.length < 2) return null;
  // 后端 DOGE token 是 payload.sig 两段；兼容旧 JWT 三段时取第二段。
  const payload = parts.length === 2 ? parts[0] : parts[1];
  const padded = payload.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - payload.length % 4) % 4);
  return JSON.parse(decodeURIComponent(escape(window.atob(padded))));
}

export default new Vuex.Store({
  state: {
    walletAddress: null, // 存储钱包地址
    walletConnected: false, // 存储钱包连接状态
    userDetails: null, // 新增：存储用户详情，例如 { username: 'xxx', totalInvestment: 'xxx', myAssets: 'xxx' }
    loadingUserDetails: false, // 新增：用户详情加载状态
    errorUserDetails: null, // 新增：用户详情错误信息
    userDetailsLoaded: false, // 地址接口是否已返回，用于避免未拉取完就误弹邀请框
    inviteCode: null, // 新增：存储从URL解析的邀请码
    loadingWallet: false, // 新增：钱包连接检查进行中状态
    walletConnectionError: null, // 新增：钱包连接错误信息
    dogeAuthToken: localStorage.getItem('doge_auth_token') || null,
    inviterBoundAddress: getLocalBoundAddress(),

    // 新增：存储全局的 provider 和 signer
    provider: null,
    signer: null,
    showBindInviterModal: false, // <--- 新增：控制弹窗的显示/隐藏


  },
  mutations: {
    // Mutation 用于同步修改 state
    setWalletAddress(state, address) {
      const oldAddress = (state.walletAddress || '').toLowerCase();
      const newAddress = (address || '').toLowerCase();
      state.walletAddress = address;
      state.walletConnected = !!address;
      if (address) localStorage.setItem('doge_wallet_address', address);
      else localStorage.removeItem('doge_wallet_address');
      if (oldAddress && newAddress && oldAddress !== newAddress) {
        state.dogeAuthToken = null;
        state.userDetails = null;
        state.errorUserDetails = null;
        state.userDetailsLoaded = false;
        state.inviterBoundAddress = '';
        localStorage.removeItem('doge_auth_token');
        localStorage.removeItem('doge_inviter_bound_address');
      }
    },
    setDogeAuthToken(state, token) {
      state.dogeAuthToken = token;
      if (token) localStorage.setItem('doge_auth_token', token);
      else localStorage.removeItem('doge_auth_token');
    },
    clearWalletAddress(state) {
      state.walletAddress = null;
      state.walletConnected = false;
      state.dogeAuthToken = null;
      localStorage.removeItem('doge_wallet_address');
      localStorage.removeItem('doge_auth_token');
      state.userDetails = null; // 钱包断开连接时，清除用户详情
      state.userDetailsLoaded = false;
      state.walletConnectionError = null; // 清除错误
    },
    setLoadingWallet(state, status) { // 新增 mutation
      state.loadingWallet = status;
    },
    setWalletConnectionError(state, error) { // 新增 mutation
      state.walletConnectionError = error;
    },
    setLoadingUserDetails(state, status) {
      state.loadingUserDetails = status;
    },
    setUserDetails(state, details) {
      state.userDetails = details;
      state.userDetailsLoaded = true;
      state.errorUserDetails = null;
    },
    setErrorUserDetails(state, error) {
      state.errorUserDetails = error;
      state.userDetailsLoaded = false;
      state.userDetails = null; // 设置错误时清除详情
    },
    clearUserDetails(state) { // 新增：清除用户详情
      state.userDetails = null;
      state.userDetailsLoaded = false;
      state.loadingUserDetails = false;
      state.errorUserDetails = null;
    },
    // 新增：设置邀请码
    setInviteCode(state, code) {
      state.inviteCode = code;
    },
    clearInviteCode(state) { // 绑定成功或无需绑定后清除
      state.inviteCode = null;
    },
    // 新增：设置 provider 和 signer 的 mutation
    setProvider(state, provider) {
      state.provider = provider;
    },
    setSigner(state, signer) {
      state.signer = signer;
    },
    setShowBindInviterModal(state, value) { // <--- 新增：修改弹窗状态的 mutation
      state.showBindInviterModal = value;
    },
    markInviterBound(state, address) {
      const normalized = normalizeAddress(address || state.walletAddress);
      state.inviterBoundAddress = normalized;
      if (normalized) localStorage.setItem('doge_inviter_bound_address', normalized);
    },
    clearInviterBound(state) {
      state.inviterBoundAddress = '';
      localStorage.removeItem('doge_inviter_bound_address');
    },
  },
  actions: {
    // 核心的钱包连接检查Action
    async checkWalletConnectionAction({ commit, dispatch, getters }) {
      if (getters.isLoadingWallet) { // 避免重复检查
        return;
      }

      commit('setLoadingWallet', true);
      commit('setWalletConnectionError', null);

      try {
        // 这段逻辑应确保在window.ethereum可用时才执行
        if (typeof window.ethereum !== 'undefined') {
          const accounts = await window.ethereum.request({ method: 'eth_accounts' });
          if (accounts.length > 0) {
            const address = accounts[0];
            const provider = new ethers.providers.Web3Provider(window.ethereum);
            const signer = await provider.getSigner();

            commit('setWalletAddress', address);
            commit('setProvider', provider); // 提交 provider
            commit('setSigner', signer);     // 提交 signer

            // 自动检查只同步已授权钱包地址，不主动签名/拉用户详情，避免进入首页前弹签名或绑定邀请人。

            // 注册事件监听器 (只注册一次)
            // 确保这些监听器不会重复注册
            if (!window.ethereum._events || !window.ethereum._events.accountsChanged) { // 简单检查是否已注册
              window.ethereum.on('accountsChanged', (newAccounts) => {
                if (newAccounts.length === 0) {
                  dispatch('disconnectWallet');
                  Vue.prototype.$messageDialog.alert('钱包已断开连接。', '提示', 'info');
                } else {
                  dispatch('updateWalletAddress', newAccounts[0]);
                  Vue.prototype.$messageDialog.alert('钱包账户已切换到: ' + Vue.prototype.formatAddress(newAccounts[0]), '账户切换', 'info');
                }
              });
              window.ethereum.on('chainChanged', (chainId) => {
                dispatch('handleChainChangedAction', chainId); // 触发新的action处理链切换
                Vue.prototype.$messageDialog.alert('网络已切换到 Chain ID: ' + parseInt(chainId, 16), '网络切换', 'info');
              });
            }
          } else {
            commit('clearWalletAddress');
            commit('clearUserDetails');
          }
        } else {
          commit('clearWalletAddress');
          commit('setWalletConnectionError', '未检测到MetaMask钱包。');
          // Silent during automatic connection checks; only show MetaMask prompts after an explicit user action.
        }
      } catch (error) {
        console.error('Wallet check failed:', error);
        commit('setWalletConnectionError', '钱包连接状态检查失败，请重新连接钱包。');
        commit('clearWalletAddress');
        commit('clearUserDetails');
      } finally {
        commit('setLoadingWallet', false);
      }
    },
    // 这是一个辅助 action，用于处理链切换，避免直接在 router.beforeEach 里放太多逻辑
    async handleChainChangedAction({ commit, dispatch }, chainId) {
      // 当链切换时，可以重新检查连接状态，重新初始化 provider/signer
      // 这里的逻辑通常在 linkWallet/checkWalletConnection 里处理
      // 或者，让它触发 fetchUserDetails 重新获取数据
      await dispatch('fetchUserDetails');
    },
    updateWalletAddress({ commit, dispatch }, address) { // 手动更新地址 (linkWallet 触发)
      commit('setWalletAddress', address);
      // 触发用户详情获取
      dispatch('fetchUserDetails');
    },
    disconnectWallet({ commit }) { // 断开连接 (handleAccountsChanged 触发)
      commit('clearWalletAddress');
      commit('clearUserDetails');
      commit('clearInviteCode');
      commit('setDogeAuthToken', null);

      commit('setProvider', null); // 确保断开时清空
      commit('setSigner', null);
    },
    async ensureDogeAuth({ commit, getters, state }, options = {}) {
      const address = getters.getWalletAddress;
      const normalizedAddress = normalizeAddress(address);
      const force = !!(options && options.force);
      const cachedToken = force ? null : (state.dogeAuthToken || localStorage.getItem('doge_auth_token'));
      if (!address || !window.ethereum) throw new Error(this._vm.$t ? this._vm.$t('buyPage.alerts.connectWalletFirst') : '请先连接钱包');
      if (!/^0x[a-fA-F0-9]{40}$/.test(address)) {
        commit('clearWalletAddress');
        throw new Error(this._vm.$t ? this._vm.$t('buyPage.alerts.invalidWalletAddress') : '钱包地址看起来不对，请重新连接钱包');
      }
      if (cachedToken) {
        try {
          const data = decodeDogeAuthPayload(cachedToken);
          const tokenAddress = data ? normalizeAddress(data.address || data.wallet_address || data.walletAddress) : '';
          const expired = data && data.exp && Number(data.exp) <= Math.floor(Date.now() / 1000);
          const addressMismatch = tokenAddress && tokenAddress !== normalizedAddress;
          if (!expired && !addressMismatch) {
            if (cachedToken !== state.dogeAuthToken) commit('setDogeAuthToken', cachedToken);
            return cachedToken;
          }
        } catch (e) {
          commit('setDogeAuthToken', null);
        }
        commit('setDogeAuthToken', null);
      }
      if (dogeAuthPromise) return dogeAuthPromise;
      dogeAuthPromise = (async () => {
        const nonceRes = await this._vm.$axios.get('/api/doge/auth/nonce', { params: { address } });
        if (![1, 200].includes(Number(nonceRes.data.code))) throw new Error(nonceRes.data.msg || nonceRes.data.message || (this._vm.$t ? this._vm.$t('buyPage.alerts.getSignatureFailed') : '钱包授权信息获取失败，请重新连接钱包'));
        const data = nonceRes.data.data || {};
        const signature = await signDogeMessage(address, data.message);
        const verifyRes = await this._vm.$axios.post('/api/doge/auth/verify', {
          address,
          nonce: data.nonce,
          message: data.message,
          signature,
        });
        if (![1, 200].includes(Number(verifyRes.data.code))) throw new Error(verifyRes.data.msg || verifyRes.data.message || (this._vm.$t ? this._vm.$t('buyPage.alerts.walletSignatureFailed') : '钱包签名没有验证通过，请重新签名'));
        const verifyData = verifyRes.data.data || {};
        const token = verifyData.token || verifyData.access_token || verifyData.auth_token || verifyRes.data.token;
        if (!token) throw new Error(this._vm.$t ? this._vm.$t('buyPage.alerts.walletLoginFailed') : '钱包登录没有成功，请重新连接钱包');
        commit('setDogeAuthToken', token);
        return token;
      })();
      try {
        return await dogeAuthPromise;
      } finally {
        dogeAuthPromise = null;
      }
    },
    // 新增：获取用户详情的 action
    async fetchUserDetails({ commit, getters, state }) {
      if (!getters.isWalletConnected) { // 如果钱包未连接，则不执行
        commit('clearUserDetails'); // 确保状态是清空的
        return;
      }
      if (userDetailsPromise) return userDetailsPromise;

      userDetailsPromise = (async () => {
        commit('setLoadingUserDetails', true);
        commit('setErrorUserDetails', null); // 清除上次错误

        const userWalletAddress = getters.getWalletAddress;

        try {
        let apiUrl;
        // 关键修改：根据环境获取 apiUrl
        if (process.env.NODE_ENV === 'production') {
          // 生产环境，从 getGlobalAddress 获取，并拼接完整的路径
          // apiUrl = `${getters.getGlobalAddress}/api/v1/user/details`;
          apiUrl = '/api/wallet/address/info?address=' + userWalletAddress;
        } else {
          // 开发环境，使用代理路径
          apiUrl = '/api/wallet/address/info?address=' + userWalletAddress;
        }

        // 假设这里使用 axios，需要确保 axios 已经全局挂载到 Vue.prototype.$axios
        const response = await this._vm.$axios.get(apiUrl); // _vm 是访问 Vue 实例的方法

        const userAccount = response.data.data && response.data.data.account;
        if ([1, 200].includes(Number(response.data.code)) && userAccount) {
          commit('setUserDetails', userAccount);
          if (isAccountInviterBound(userAccount)) {
            commit('markInviterBound', userWalletAddress);
            commit('setShowBindInviterModal', false);
          } else {
            // 已注册但未绑定邀请人的账户仍然必须弹窗，且清掉上版误写入的本地“已绑定”标记。
            commit('clearInviterBound');
            commit('setShowBindInviterModal', true);
          }
        } else {
          // 新用户/未注册地址：先弹绑定邀请人；确认绑定时再签名并调用 upload 创建账户。
          commit('setUserDetails', { address: userWalletAddress, parent_id: 0 });
          commit('setShowBindInviterModal', true);
        }
      } catch (error) {
        commit('setErrorUserDetails', '账户信息加载失败，请检查网络后重试。');
        console.error('Fetch User Details Error:', error);
        } finally {
          commit('setLoadingUserDetails', false);
        }
      })();
      try {
        return await userDetailsPromise;
      } finally {
        userDetailsPromise = null;
      }
    },
    // 新增：绑定邀请关系的 action
    async bindReferralRelationship({ commit, dispatch, getters, state }, { inviteCode, address }) {
      if (!inviteCode || !address) {
        return;
      }

      try {
        let apiUrl;
        // 关键修改：根据环境获取 apiUrl
        if (process.env.NODE_ENV === 'production') {
          // 生产环境，从 getGlobalAddress 获取，并拼接完整的路径
          // apiUrl = `${getters.getGlobalAddress}/api/v1/user/details`;
          apiUrl = '/api/wallet/address/upload';
        } else {
          // 开发环境，使用代理路径
          apiUrl = '/api/wallet/address/upload';
        }

        const requestBody = {
          inviteCode: inviteCode,
          address: address // 后端接收的钱包地址字段名
        };

        const response = await this._vm.$axios.post(apiUrl, requestBody); // 使用 _vm.$axios 发送 POST 请求

        if ([1, 200].includes(Number(response.data.code))) {
          commit('markInviterBound', address);
          commit('setShowBindInviterModal', false);
          commit('clearInviteCode'); // 绑定成功后清除邀请码
          localStorage.removeItem('inviteCode'); // 从 localStorage 清除
          await dispatch('fetchUserDetails');
          // 可以选择弹窗提示
        } else {
          console.error('绑定邀请关系失败:', response.data);
        }
      } catch (error) {
        console.error('绑定邀请关系错误:', error);
      }
    }
  },
  getters: {
    getWalletAddress: state => state.walletAddress,
    isWalletConnected: state => state.walletConnected,
    isLoadingWallet: state => state.loadingWallet,
    getWalletConnectionError: state => state.walletConnectionError,
    getUserDetails: state => state.userDetails,
    isLoadingUserDetails: state => state.loadingUserDetails,
    getUserDetailsError: state => state.errorUserDetails,
    isUserDetailsLoaded: state => state.userDetailsLoaded,
    // 新增：获取挂载在 Vue.prototype 上的 $address
    getGlobalAddress: () => Vue.prototype.$address, // 或者 this._vm.$address
    getInviteCode: state => state.inviteCode, // 新增 getter
    getDogeAuthToken: state => state.dogeAuthToken,

    // 新增：获取 provider 和 signer
    getProvider: state => state.provider,
    getSigner: state => state.signer,

isInviterBound: state => {
  const wallet = normalizeAddress(state.walletAddress);
  // 如果接口已经返回账户详情，以接口的上级绑定状态为准；本地标记只作为刚绑定成功/接口暂未返回时的临时态。
  if (state.userDetails) return isAccountInviterBound(state.userDetails);
  return !!(wallet && state.inviterBoundAddress && normalizeAddress(state.inviterBoundAddress) === wallet);
},

  }
});
